/**
 * PhoneCaptureModal — single-field modal for capturing a user's mobile number.
 *
 * Two modes:
 *   - default (dismissible): a soft nudge. ✕ / "Maybe later" close it.
 *   - `required` (blocking): used by <PhoneGate> at the app shell. No ✕, no ESC,
 *     no backdrop close. The only ways out are saving a valid number, an optional
 *     "Remind me later" (admins only), or "Sign out".
 *
 * On submit: PUT /user/:id with { phoneNumber: <E.164>, smsOptIn: true }, update
 * the Redux user, close. The number is normalised to E.164 (utils/phone.js) so it
 * always passes the server's libphonenumber validation.
 *
 * A mobile number is required so players receive performance alerts and account
 * notifications by text — saving the number opts the user in; there is no
 * separate checkbox.
 */
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { Dialog, Divider, CircularProgress } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTheme } from "@material-ui/core";

import SoftBox from "../SoftBox";
import SoftTypography from "../SoftTypography";
import SoftInput from "../SoftInput";
import SoftButton from "../SoftButton";

import { UseUserDetails } from "../../hooks/auth";
import { updateUserDetails } from "../../redux/auth/actions";
import { PUT_USER } from "../../api/user";
import { toE164, PHONE_EXAMPLE, PHONE_ERROR } from "../../utils/phone";

export default function PhoneCaptureModal({
  open,
  onClose,
  required = false,
  allowRemindLater = false,
  onRemindLater,
  onSignOut,
}) {
  const dispatch = useDispatch();
  const user = UseUserDetails();
  const theme = useTheme();
  const {
    primary: { main: primaryColor },
  } = theme.palette;

  const [value, setValue] = useState(user?.phoneNumber || "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const e164 = toE164(value);
    if (!e164) {
      setError(PHONE_ERROR);
      return;
    }
    setError("");
    setSaving(true);
    try {
      await PUT_USER(user?._id, { phoneNumber: e164, smsOptIn: true });
      // Only merge the fields we know — the PUT response can come back wrapped
      // in Mongoose-doc internals (_doc / $__), which must not leak into Redux.
      dispatch(updateUserDetails({ phoneNumber: e164, smsOptIn: true }));
      toast.success("Phone number saved.");
      onClose?.();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Could not save phone number.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={required || saving ? undefined : onClose}
      disableEscapeKeyDown={required}
      sx={{
        margin: "auto",
        // Clear the mobile sidebar overlay (z-[9998]/[9999] in Main.jsx) and any
        // other MUI modal when this is the blocking gate.
        zIndex: required ? 13000 : undefined,
        width: { xs: "100vw", sm: "60vw" },
        ".MuiPaper-root": {
          borderRadius: "24px",
          width: "100%",
          maxWidth: "520px",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.60) 100%)",
          boxShadow:
            "0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
          backdropFilter: "blur(18px) saturate(160%)",
          padding: { mini: "1.25rem", md: "1.75rem" },
          overflow: "visible",
          border: "1px solid rgba(255,255,255,0.6)",
        },
      }}
    >
      <SoftBox
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap="12px"
      >
        <SoftBox sx={{ minWidth: 0 }}>
          <SoftTypography
            sx={{
              fontFamily: "DM Sans",
              fontSize: "17px",
              fontWeight: 700,
              letterSpacing: "-0.01em",
              lineHeight: 1.25,
              color: "#0F172A",
            }}
          >
            {required
              ? "Add your phone number to continue"
              : "Add your phone number"}
          </SoftTypography>
          <SoftTypography
            sx={{
              fontFamily: "DM Sans",
              fontSize: "12.5px",
              fontWeight: 400,
              color: "#475467",
              marginTop: "3px",
              lineHeight: 1.5,
            }}
          >
            {required
              ? "Game My Biz now requires a mobile number on your profile. "
              : "Optional. "}
            You&rsquo;ll receive recurring text messages at this number &mdash;
            performance alerts, scorecards, leaderboards, achievements, and
            account notifications. Message frequency varies. Message and data
            rates may apply. See{" "}
            <a
              href="https://gamemybiz.com/terms-condition"
              target="_blank"
              rel="noreferrer"
              style={{ color: "#2563EB", textDecoration: "underline" }}
            >
              SMS Terms
            </a>
            {" "}and{" "}
            <a
              href="https://gamemybiz.com/privacy-policy"
              target="_blank"
              rel="noreferrer"
              style={{ color: "#2563EB", textDecoration: "underline" }}
            >
              Privacy Policy
            </a>
            .
          </SoftTypography>
        </SoftBox>
        {!required && (
          <SoftButton
            variant="outlined"
            onClick={onClose}
            disabled={saving}
            sx={{
              "& svg": { fontSize: "1.1rem !important" },
              borderRadius: "50%",
              minWidth: "32px",
              minHeight: "32px",
              width: "32px",
              height: "32px",
              padding: 0,
              flexShrink: 0,
              background: "rgba(15,23,42,0.05) !important",
              border: "1px solid rgba(15,23,42,0.10)",
              color: "#475467",
            }}
          >
            <CloseRoundedIcon />
          </SoftButton>
        )}
      </SoftBox>

      <Divider sx={{ backgroundColor: "rgba(15,23,42,0.10)", marginY: "16px" }} />

      <SoftInput
        type="tel"
        autoFocus
        placeholder={PHONE_EXAMPLE}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        error={!!error}
        helperText={error}
        sx={{
          border: "1px solid #E4E7EC",
          backgroundColor: "#F6F7F9 !important",
          color: "#1d2939 !important",
          "& .MuiInputBase-input::placeholder": {
            color: "#98A2B3 !important",
            opacity: 1,
          },
          "&.Mui-focused": {
            borderColor: primaryColor,
            boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}`,
          },
        }}
        size="large"
      />

      <SoftBox
        display="flex"
        justifyContent={required && (allowRemindLater || onSignOut) ? "space-between" : "flex-end"}
        alignItems="center"
        gap="0.75rem"
        sx={{ marginTop: "1.5rem", flexWrap: "wrap" }}
      >
        {required ? (
          <SoftBox display="flex" gap="0.5rem">
            {onSignOut && (
              <SoftButton
                variant="text"
                onClick={onSignOut}
                disabled={saving}
                sx={{
                  color: "#98A2B3",
                  textTransform: "none",
                  padding: "8px 6px",
                  fontFamily: "DM Sans",
                  fontSize: "12.5px",
                  fontWeight: 600,
                  background: "transparent",
                  "&:hover": { background: "transparent", color: "#475467" },
                }}
              >
                Sign out
              </SoftButton>
            )}
          </SoftBox>
        ) : (
          <SoftButton
            variant="outlined"
            onClick={onClose}
            disabled={saving}
            sx={{
              color: "#475467",
              borderRadius: "8px",
              textTransform: "none",
              padding: "8px 16px",
              fontFamily: "DM Sans",
              fontSize: "13px",
              fontWeight: 600,
              border: "1px solid rgba(15,23,42,0.10)",
              background: "transparent",
              "&:hover": { background: "rgba(15,23,42,0.04)" },
            }}
          >
            Maybe later
          </SoftButton>
        )}

        <SoftBox display="flex" gap="0.75rem" alignItems="center">
          {required && allowRemindLater && (
            <SoftButton
              variant="outlined"
              onClick={onRemindLater}
              disabled={saving}
              sx={{
                color: "#475467",
                borderRadius: "8px",
                textTransform: "none",
                padding: "8px 16px",
                fontFamily: "DM Sans",
                fontSize: "13px",
                fontWeight: 600,
                border: "1px solid rgba(15,23,42,0.10)",
                background: "transparent",
                "&:hover": { background: "rgba(15,23,42,0.04)" },
              }}
            >
              Remind me later
            </SoftButton>
          )}
          <SoftButton
            variant="contained"
            color="white"
            onClick={handleSave}
            disabled={saving}
            sx={{
              background: `${primaryColor} !important`,
              color: "#FFFFFF !important",
              borderRadius: "8px",
              textTransform: "none",
              padding: "8px 18px",
              fontFamily: "DM Sans",
              fontSize: "13px",
              fontWeight: 700,
              "&:hover": { filter: "brightness(1.08)" },
            }}
          >
            Save number
            {saving && (
              <CircularProgress
                color="inherit"
                size="0.9rem"
                sx={{ marginLeft: "0.5rem" }}
              />
            )}
          </SoftButton>
        </SoftBox>
      </SoftBox>
    </Dialog>
  );
}
